<template>
  <section>
    <div
      v-if="tenancyEditorsLoading"
      class="mass-rental-loading-overlay">
      <coozzy-spinner />
    </div>
    <div>
      <h1
        v-if="!loading">
        {{ $t('message.massRental.title') }}
      </h1>
    </div>
    <div
      class="row">
      <div
        v-if="loading"
        class="col-12 text-center">
        <coozzy-spinner />
      </div>
      <div
        v-show="!loading"
        class="col-12">
        <form-wizard
          id="wizard"
          ref="wizard"
          title=""
          subtitle=""
          color="rgb(0, 68, 95)"
          step-size="xs">
          <template #title>
            <span />
          </template>
          <template>
            <tab-content
              :title="$t('message.massRental.selectObjects')">
              <create-mass-rental-objects-table
                ref="create-mass-rental-objects-table"
                :building-ids="buildingIds"
                :buildings="listBuildings"
                :contacts="listContacts"
                :loading="objectsLoading"
                :objects="listObjects"
                :source="source"
                :tenancies="listTenancies"
                :request-object-id="requestObjectId"
                :initial-desired-start-of-contract="initialDesiredStartOfContract"
                :selected-object-ids="selectedObjectIds"
                @desired-start-date-changed="onDesiredStartDateChanged"
                @selected-objects="onSelectedObjectsChanged" />
            </tab-content>
            <tab-content
              :title="$t('message.addTenancy.tenants')">
              <add-tenancy-tenant-tab
                :billing-contact="billingContact"
                :correspondence-contact="correspondenceContact"
                :guarantor-contacts="guarantorContacts"
                :owner-id="owner ? owner.id : 0"
                :require-tenant-address="true"
                :tenant-to-hide="!billingContact ? tenantContacts : [billingContact].concat(tenantContacts)"
                :tenants="tenantContacts"
                @tenant-changed="tenantChanged"
                @tenant-removed="tenantRemoved"
                @contact-changed="additionalContactChanged"
                @contact-removed="removeAdditionalContact" />
            </tab-content>
            <template v-if="objectsByCategory">
              <template
                v-for="(items, category, index) in objectsByCategory">
                <tab-content
                  v-if="items.length > 0"
                  :key="category"
                  :title="$t('message.massRental.editTenanciesByCat', { category: $t('message.onBoarding.buildings.objects.category.' + category) })">
                  <template
                    v-for="(item, indexObject) in items">
                    <add-tenancy-tenancy-tab
                      v-if="tenancyEditorsMounted"
                      :id="'tenancy-tab-' + index + '-' + category"
                      :key="'tenancy_tab_' + item.id"
                      :ref="'tenancy-tab-' + index"
                      :billing-contact="billingContact"
                      :correspondence-contact="correspondenceContact"
                      :from-mass-rental-process="true"
                      :guarantor-contacts="guarantorContacts"
                      :initial-tenancy="getDraftTenancyForObject(item.id)"
                      :last-tenancies="getLastTenancies(item.id)"
                      :list-tenancies="listTenancies"
                      :object="item"
                      :mass-rental-property="getPropertyForObject(item.id)"
                      :tenancy-id="getTenancyId(item.id)"
                      :tenant-contacts="tenantContacts"
                      @previous-tenancy-updated="previousTenancyUpdated"
                      @updated-tenancy="tenancyUpdated" />

                    <div
                      v-if="tenancyEditorsMounted && items.length > 1 && indexObject === 0"
                      :key="'button_' + item.id"
                      class="row">
                      <div class="col-12 mb-3 text-align-end">
                        <coozzy-button
                          class="mb-0 border float-right"
                          design="prop-green"
                          size="small"
                          @click="copyData(items[0], category, index)">
                          {{ $t('message.enterTermination.takeData') }}
                        </coozzy-button>
                      </div>
                    </div>
                  </template>
                </tab-content>
              </template>
            </template>
            <tab-content
              :title="$t('message.addTenancy.createContract')">
              <div
                v-if="showSeparateDocumentsOption"
                class="mb-3">
                <label>
                  {{ $t('message.massRental.separateDocumentsQuestion') }}
                </label>
                <div class="row mt-2">
                  <div class="col-2">
                    <coozzy-form-radio
                      v-model="generateSeparateDocumentsPerObject"
                      :select-value="true"
                      class="p-0 btn likeBtn"
                      name="generateSeparateDocumentsPerObject">
                      {{ $t('message.generic.selectField.yes') }}
                    </coozzy-form-radio>
                  </div>
                  <div class="col-2">
                    <coozzy-form-radio
                      v-model="generateSeparateDocumentsPerObject"
                      :select-value="false"
                      class="p-0 btn likeBtn"
                      name="generateSeparateDocumentsPerObject">
                      {{ $t('message.generic.selectField.no') }}
                    </coozzy-form-radio>
                  </div>
                </div>
                <p
                  v-if="!generateSeparateDocumentsPerObject"
                  class="font-italic mt-2 mb-0">
                  {{ $t('message.massRental.separateDocumentsNoNote') }}
                </p>
              </div>

              <template v-if="showSeparateDocumentsOption && generateSeparateDocumentsPerObject">
                <div
                  v-for="(object, index) in createDocumentObjects"
                  :key="object ? object.id : index"
                  class="mb-1">
                  <div
                    v-b-toggle="`select-contract-${object ? object.id : index}`"
                    class="collapse-title -mb-3">
                    <h5 class="cursor-pointer">
                      {{ $t('message.enterTermination.createDocumentFor') + ' ' + object.number + ' - ' + object.name }}
                    </h5>
                  </div>
                  <b-collapse
                    :id="`select-contract-${object ? object.id : index}`"
                    :ref="`select-contract-${object ? object.id : index}`"
                    :visible="true">
                    <template-table
                      :object-id="object.id"
                      :by-owners-ids="templateOwnersIdsForObject(object)"
                      :default-category="'TEMPLATE_CATEGORY_CONTRACTS'"
                      @selected-contract="(value, contracts) => selectedContractPerObject(value, contracts, object.id)" />
                  </b-collapse>
                  <hr
                    v-if="index != createDocumentObjects.length - 1"
                    class="mt-0">
                </div>
                <div class="row mt-2">
                  <div class="col">
                    <coozzy-form-checkbox
                      v-if="isEditor && hasDocumentsPortal && addDocumentToContact"
                      v-model="showInPortal"
                      class="float-right pr-3 mt-1 mr-0 display-none-xs font-weight-lighter">
                      {{ $t('message.generic.showInPortal') }}
                      <coozzy-info-circle-icon
                        v-if="!isPortalLinkEnabled"
                        v-b-tooltip.hover.html="$t('message.manageDocuments.tooltipShowInPortal')"
                        class="ml-1" />
                    </coozzy-form-checkbox>
                    <coozzy-form-checkbox
                      v-model="addDocumentToContact"
                      class="float-right pr-3 mt-1 mr-0 display-none-xs font-weight-lighter">
                      {{ $t('message.generic.addDocumentToTenancy') }}
                    </coozzy-form-checkbox>
                  </div>
                </div>
              </template>

              <add-tenancy-contract-tab
                v-else
                :owner-id="owner ? owner.id : 0"
                :is-portal-link-enabled="isPortalLinkEnabled"
                @attach-tenancy="addDocumentToTenancy"
                @show-in-portal="showInPortalMethod"
                @selected-contract="contractSelectedAction" />
            </tab-content>
          </template>
          <template
            #footer="props">
            <template>
              <div class="wizard-footer-left">
                <wizard-button
                  :disabled="disableButtons"
                  :style="props.fillButtonStyle"
                  class="mr-2"
                  @click.native="$router.back()">
                  {{ $t('message.generic.cancel') }}
                </wizard-button>
                <wizard-button
                  v-if="props.activeTabIndex > 0"
                  :disabled="disableButtons || tenancyEditorsLoading"
                  :style="props.fillButtonStyle"
                  @click.native="backClicked(props)">
                  {{ $t('message.generic.back') }}
                </wizard-button>
              </div>
              <div class="wizard-footer-right">
                <template
                  v-if="!props.isLastStep">
                  <wizard-button
                    v-if="props.activeTabIndex <= 1"
                    :disabled="isNextDisabled(props.activeTabIndex)"
                    :style="props.fillButtonStyle"
                    class="wizard-footer-right"
                    @click.native="nextStep(props)">
                    <template>
                      {{ $t('message.generic.next') }}
                    </template>
                  </wizard-button>
                  <wizard-button
                    v-else-if="props.activeTabIndex !== countTabs - 2"
                    :disabled="isNextDisabled(props.activeTabIndex)"
                    :style="props.fillButtonStyle"
                    class="wizard-footer-right"
                    @click.native="nextStepWithValidation(props)">
                    <template>
                      {{ $t('message.generic.next') }}
                    </template>
                  </wizard-button>
                  <wizard-button
                    v-else
                    :disabled="isNextDisabled(props.activeTabIndex)"
                    :style="props.fillButtonStyle"
                    class="wizard-footer-right"
                    @click.native="saveTenancies(props)">
                    <template>
                      {{ $t('message.enterTermination.saveAndNext') }}
                    </template>
                  </wizard-button>
                </template>
                <wizard-button
                  v-else
                  :disabled="disableButtons"
                  :style="props.fillButtonStyle"
                  class="wizard-footer-right"
                  @click.native="renderContract">
                  <template v-if="hasSelectedContracts">
                    {{ $t('message.addTenancy.createContractAndSave') }}
                  </template>
                  <template v-else>
                    {{ $t('message.addTenancy.createWithoutContract') }}
                  </template>
                </wizard-button>
              </div>
            </template>
          </template>
        </form-wizard>
      </div>
    </div>
  </section>
</template>

<script lang="ts">
import Vue from 'vue'
import ObjectApi from '@/misc/apis/ObjectApi'
import { syncSubTenantEndingDates } from '@/misc/utils/tenancyUtils'
import ContactApi from '@/misc/apis/ContactApi'
import CreateMassRentalObjectsTable from '@/massRental/components/CreateMassRentalObjectsTable.vue'
import UserApi from '@/misc/apis/UserApi'
import CoozzySpinner from '@/framework/components/misc/CoozzySpinner.vue'
import AddTenancyTenantTab from '@/addTenancy/AddTenancyTenantTab.vue'
import AddTenancyTenancyTab from '@/addTenancy/AddTenancyTenancyTab.vue'
import CoozzyButton from '@/framework/components/button/CoozzyButton.vue'
import { dateUtils } from '@/mixins/dateUtils'
import AddTenancyContractTab from '@/addTenancy/AddTenancyContractTab.vue'
import TemplateTable from '@/settings/components/TemplateTable.vue'
import CoozzyFormCheckbox from '@/framework/components/form/checkbox/CoozzyFormCheckbox.vue'
import CoozzyInfoCircleIcon from '@/framework/components/icons/CoozzyInfoCircleIcon.vue'
import CoozzyFormRadio from '@/framework/components/form/radio/CoozzyFormRadio.vue'
import { helper } from '@/types/helper'
// import types from api.ts
import {
  V2Account, ObjectbuildingBuilding,
  Contactcontactv2Company, V2ResolveResponse, Contractv2ContractTemplate, GooglerpcStatus,
  ObjectobjectObject, Propertyv2Property, ObjecttenancyCreateResponse,
  ObjecttenancyTenancy,
  TypeDate, V2Person
} from '@/types/api'
import { mapMutations } from 'vuex'
import { onboarding } from '@/mixins/onboarding'
import MediaApi from '@/misc/apis/MediaApi'
// @ts-ignore
import { saveAs } from 'file-saver'
import { routeChecks } from '@/mixins/routeChecks'
import { user } from '@/mixins/user'
import RequestApi from '@/marketingBasic/api/RequestApi'
import debounce from 'debounce'

interface WizardParams {
  activeTabIndex: number;
  prevTab: () => void;
  nextTab: () => void;
  isLastStep: boolean;
  fillButtonStyle: string;
}

type Contact = V2Person | Contactcontactv2Company

type ObjectsByCategory = {
  [key: string]: ObjectobjectObject[];
}
type SelectedContractsByObject = {
  [key: string]: {
    ids: string[],
    contracts: Contractv2ContractTemplate[]
  }
}

export default Vue.extend({
  name: 'CreateMassRentalView',
  components: {
    CoozzyFormRadio,
    CoozzyInfoCircleIcon,
    CoozzyFormCheckbox,
    TemplateTable,
    AddTenancyContractTab,
    CoozzyButton,
    AddTenancyTenancyTab,
    AddTenancyTenantTab,
    CoozzySpinner,
    CreateMassRentalObjectsTable
  },
  metaInfo() {
    return {
      title: this.$t('message.massRental.title') + (this.sourceName !== '' ? ': ' + this.sourceName : '')
    }
  },
  mixins: [dateUtils, onboarding, routeChecks, user],
  props: {
    source: {
      type: String,
      required: true
    },
    buildingIds: {
      type: String,
      required: true
    },
    tenantIds: {
      type: String,
      default: ''
    },
    requestId: {
      type: String,
      default: ''
    },
    requestObjectId: {
      type: String,
      default: ''
    },
    tenancyIds: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      listObjects: [] as ObjectobjectObject[],
      listObjectsFromTenancies: [] as ObjectobjectObject[],
      listObjectsFromRequest: [] as ObjectobjectObject[],
      loading: false as boolean,
      objectsLoading: false as boolean,
      enrichmentLoading: false as boolean,
      listTenancies: [] as ObjecttenancyTenancy[],
      listContacts: [] as Contact[],
      owner: null as V2Account,
      pendingSelectedObjects: [] as ObjectobjectObject[],
      objectsByCategory: null as ObjectsByCategory,
      tenancyEditorsLoading: false as boolean,
      tenancyEditorsMounted: false as boolean,
      tenantContacts: [] as Contact[],
      billingContact: null as Contact,
      correspondenceContact: null as Contact,
      guarantorContacts: [] as Contact[],
      initialTenancies: [] as ObjecttenancyTenancy[],
      newTenancies: [] as ObjecttenancyTenancy[],
      createdTenancies: [] as ObjecttenancyTenancy[],
      listProperties: [] as Propertyv2Property[],
      listBuildings: [] as ObjectbuildingBuilding[],
      countTabs: 0 as number,
      emptyTenancy: helper.emptyTenancy,
      disableButtons: false as boolean,
      title: '' as string,
      description: '' as string,
      addDocumentToContact: false as boolean,
      showInPortal: false as boolean,
      generateSeparateDocumentsPerObject: false as boolean,
      contractSelected: [] as string[],
      contracts: [] as Contractv2ContractTemplate[],
      selectedContractsByObject: {} as SelectedContractsByObject,
      initialDesiredStartOfContract: '' as string,
      objectsLoadGeneration: 0 as number,
      previousTenanciesToUpdate: [] as ObjecttenancyTenancy[]
    }
  },
  computed: {
    tempId() {
      return Math.random().toString(36).substring(2, 9)
    },
    sourceName() {
      if (this.listProperties === 1) {
        return this.listProperties[0].name
      } else if (this.listBuildings.length === 1) {
        return this.listBuildings[0].name
      }
      return ''
    },
    hasSelectedContracts() {
      if (this.showSeparateDocumentsOption && this.generateSeparateDocumentsPerObject) {
        const selections = Object.values(this.selectedContractsByObject) as Array<{ ids: string[] }>
        return selections.some(entry => entry.ids && entry.ids.length > 0)
      }
      return this.contractSelected.length > 0
    },
    showSeparateDocumentsOption() {
      return this.isAdminModule || this.isMarketingModule
    },
    createDocumentObjects(): ObjectobjectObject[] {
      if (!this.createdTenancies.length || !this.listObjects.length) {
        return []
      }
      const createdObjectIds = this.createdTenancies.map((tenancy: ObjecttenancyTenancy) => tenancy.objectId)
      return this.listObjects.filter((obj: ObjectobjectObject) => createdObjectIds.includes(obj.id))
    },
    tenantContactsMissingAddress(): boolean {
      return this.tenantContacts.some((x: Contact) => !(x.address?.zip && x.address?.city && x.address?.country))
    },
    selectedObjectIds(): string[] {
      return this.pendingSelectedObjects.map((obj: ObjectobjectObject) => obj.id).filter((id: string) => id)
    },
    hasPendingSelectedObjects(): boolean {
      return this.pendingSelectedObjects.length > 0
    }
  },
  created() {
    const debounced = debounce((startDate: string) => {
      this.loadObjects(startDate).catch((e) => console.log(e))
    }, 280)
    this.debouncedLoadObjectsFromDesiredDate = debounced
  },
  mounted() {
    this.loadProperties()
    this.loadBuildings()
    if (this.tenantIds !== '') {
      this.loadRequestTenantContacts()
    }
    if (this.tenancyIds !== '') {
      this.loadDataForEditing()
    }
    if (this.requestObjectId !== '') {
      this.loadRequestObjectCache().catch((e) => console.log(e))
    }
  },
  methods: {
    ...mapMutations('onboarding', ['setObjectOwnerContacts']),
    cloneTenancy(tenancy: ObjecttenancyTenancy): ObjecttenancyTenancy {
      return JSON.parse(JSON.stringify(tenancy)) as ObjecttenancyTenancy
    },
    isEditableDraftTenancy(tenancy: ObjecttenancyTenancy): boolean {
      return !this.listTenancies.some((lt: ObjecttenancyTenancy) => lt === tenancy)
    },
    forEachEditableDraftTenancy(callback: (t: ObjecttenancyTenancy) => void) {
      this.initialTenancies.forEach((t: ObjecttenancyTenancy) => {
        if (this.isEditableDraftTenancy(t)) {
          callback(t)
        }
      })
    },
    copyDraftTenantFields(from: ObjecttenancyTenancy, to: ObjecttenancyTenancy) {
      if (!from?.tenant || !to?.tenant) {
        return
      }
      to.tenant.billingContactId = from.tenant.billingContactId
      to.tenant.contactIds = [...(from.tenant.contactIds || [])]
      to.tenant.correspondenceContactId = from.tenant.correspondenceContactId
      to.tenant.guarantorContactIds = [...(from.tenant.guarantorContactIds || [])]
    },
    getSelectedObjectIds(): Set<string> {
      if (this.objectsByCategory) {
        return new Set(Object.values(this.objectsByCategory).flat().map((o: ObjectobjectObject) => o.id))
      }
      return new Set(this.pendingSelectedObjects.map((o: ObjectobjectObject) => o.id))
    },
    getPropertyForObject(objectId: string): Propertyv2Property | null {
      let building = this.listBuildings.find((b: ObjectbuildingBuilding) => b.objectIds?.includes(objectId))
      if (!building) {
        const object = this.listObjects.find((o: ObjectobjectObject) => o.id === objectId)
        if (object?.buildingId) {
          building = this.listBuildings.find((b: ObjectbuildingBuilding) => b.id === object.buildingId)
        }
      }
      if (!building) {
        return null
      }
      return this.listProperties.find((p: Propertyv2Property) => p.buildingIds?.includes(building.id)) || null
    },
    getDraftTenancyForObject(objectId: string): ObjecttenancyTenancy | null {
      const fromNew = this.newTenancies.find((t: ObjecttenancyTenancy) => t.objectId === objectId)
      if (fromNew) {
        return fromNew
      }
      return this.initialTenancies.find((t: ObjecttenancyTenancy) => t.objectId === objectId) || null
    },
    reapplySelectedObjectsFromTable() {
      const tableRef = this.$refs['create-mass-rental-objects-table'] as { selectedEntries?: ObjectobjectObject[] } | undefined
      const selectedIds = new Set((tableRef?.selectedEntries || []).map((o: ObjectobjectObject) => o.id))
      if (selectedIds.size === 0) {
        this.onSelectedObjectsChanged([])
        return
      }
      const currentSelection = this.listObjects.filter((o: ObjectobjectObject) => selectedIds.has(o.id))
      this.onSelectedObjectsChanged(currentSelection)
    },
    isNextDisabled(activeTabIndex: number): boolean {
      if (this.disableButtons || this.objectsLoading || this.enrichmentLoading || this.tenancyEditorsLoading) {
        return true
      }
      if (activeTabIndex === 0) {
        return !this.hasPendingSelectedObjects
      }
      return !this.objectsByCategory
    },
    resetTenancyEditorsMountState() {
      this.tenancyEditorsLoading = false
      this.tenancyEditorsMounted = false
    },
    async yieldForPaint(): Promise<void> {
      await this.$nextTick()
      await new Promise<void>((resolve) => {
        window.setTimeout(() => resolve(), 50)
      })
    },
    onSelectedObjectsChanged(objects: ObjectobjectObject[]) {
      const uniqueById: ObjectobjectObject[] = Array.from(
        new Map<string, ObjectobjectObject>(
          objects.map((o: ObjectobjectObject) => [o.id, o] as [string, ObjectobjectObject])
        ).values()
      )
      this.pendingSelectedObjects = uniqueById
      if (uniqueById.length === 0) {
        this.objectsByCategory = null
        this.resetTenancyEditorsMountState()
      }
    },
    commitPendingSelectionToCategories() {
      const objects: ObjectobjectObject[] = this.pendingSelectedObjects
      if (objects.length === 0) {
        this.objectsByCategory = null
        this.resetTenancyEditorsMountState()
        return
      }
      const uniqueById: ObjectobjectObject[] = Array.from(
        new Map<string, ObjectobjectObject>(
          objects.map((o: ObjectobjectObject) => [o.id, o] as [string, ObjectobjectObject])
        ).values()
      )
      const sortedObjects = uniqueById.sort((a: ObjectobjectObject, b: ObjectobjectObject) => {
        return this.customSort(a.category) - this.customSort(b.category)
      })
      this.objectsByCategory = sortedObjects.reduce((result: ObjectsByCategory, obj: ObjectobjectObject) => {
        const category = obj.category.toLowerCase()
        if (!result[category]) {
          result[category] = []
        }
        result[category].push(obj)
        return result
      }, {} as ObjectsByCategory)
      if (this.initialTenancies.length > 0) {
        uniqueById.forEach((element: ObjectobjectObject) => {
          if (this.tenancyIds !== '') {
            const tenancy = this.listTenancies.find((x: ObjecttenancyTenancy) => x.objectId === element.id && this.tenancyIds.split(',').includes(x.id))
            if (tenancy && !this.newTenancies.find((x: ObjecttenancyTenancy) => x.objectId === element.id)) {
              this.newTenancies.push(this.cloneTenancy(tenancy))
            } else {
              const draft = this.initialTenancies.find((x: ObjecttenancyTenancy) => x.objectId === element.id)
              if (draft && !this.newTenancies.find((x: ObjecttenancyTenancy) => x.objectId === element.id)) {
                this.newTenancies.push(this.cloneTenancy(draft))
              }
            }
          } else {
            const draft = this.initialTenancies.find((x: ObjecttenancyTenancy) => x.objectId === element.id)
            if (draft && !this.newTenancies.find((x: ObjecttenancyTenancy) => x.objectId === element.id)) {
              this.newTenancies.push(this.cloneTenancy(draft))
            }
          }
        })
      }
    },
    syncNewTenanciesFromDrafts() {
      const selectedObjectIds = this.getSelectedObjectIds()
      this.forEachEditableDraftTenancy((draft: ObjecttenancyTenancy) => {
        if (selectedObjectIds.size > 0 && !selectedObjectIds.has(draft.objectId)) {
          return
        }
        const idx = this.newTenancies.findIndex((nt: ObjecttenancyTenancy) => nt.objectId === draft.objectId)
        if (idx === -1) {
          this.newTenancies.push(this.cloneTenancy(draft))
          return
        }
        this.copyDraftTenantFields(draft, this.newTenancies[idx])
        Vue.set(this.newTenancies, idx, this.newTenancies[idx])
      })
    },
    getTenancyId(objId: string) {
      if (this.tenancyIds !== '') {
        const splitIds = this.tenancyIds.split(',')
        const tenancy = this.listTenancies.find((x: ObjecttenancyTenancy) => x.objectId === objId && splitIds.includes(x.id))
        if (tenancy) {
          return tenancy.id
        }
      } else {
        return ''
      }
    },
    async loadDataForEditing() {
      this.loading = true
      await this.loadTenancies()
      this.initialDesiredStartOfContract = this.$options.filters.objectToDateInput(this.listTenancies[0]?.startingAt)
      await this.loadTenanciesObjects()
      await this.loadObjects(this.initialDesiredStartOfContract)
      await this.$nextTick()
      const splitIds = this.tenancyIds.split(',')
      const objIds = this.listTenancies
        .filter((t: ObjecttenancyTenancy) => splitIds.includes(t.id))
        .map((t: ObjecttenancyTenancy) => t.objectId)
      this.$refs['create-mass-rental-objects-table']?.selectObject(objIds)
      this.commitPendingSelectionToCategories()
      this.loading = false
      this.$refs.wizard?.nextTab()
    },
    async loadTenanciesObjects() {
      const ids = this.listTenancies.map((t: ObjecttenancyTenancy) => t.objectId)
      if (ids.length > 0) {
        const rspObjects = await ObjectApi.listObjectsByIds(ids)
        this.listObjectsFromTenancies = rspObjects.objects
      }
    },
    async loadTenancies() {
      const ids = this.tenancyIds.split(',')
      if (ids.length > 0) {
        const tenancyResponse = await ObjectApi.getTenanciesByIds(ids)

        const list: ObjecttenancyTenancy[] = this.listTenancies.concat(tenancyResponse.tenancies)

        this.listTenancies = [...new Set(list.map((a) => a.id))].map(id => {
          return list.find((a) => a.id === id)
        })
      }
    },
    tenancyUpdated(tenancy: ObjecttenancyTenancy) {
      const index = this.newTenancies.findIndex((t: ObjecttenancyTenancy) => t.objectId === tenancy.objectId)
      if (index !== -1) {
        this.newTenancies[index] = tenancy
      } else {
        this.newTenancies.push(tenancy)
      }
    },
    copyData(object?: ObjectobjectObject, category?: string | number, categoryIndex?: number) {
      if (object && category) {
        let tenancy: ObjecttenancyTenancy | undefined
        if (categoryIndex !== undefined) {
          const tabs = this.$refs['tenancy-tab-' + categoryIndex] as Array<{ tenancy: ObjecttenancyTenancy }> | undefined
          tenancy = tabs?.[0]?.tenancy
        }
        if (!tenancy) {
          tenancy = this.newTenancies.find((x: ObjecttenancyTenancy) => x.objectId === object.id)
        }
        if (!tenancy) {
          return
        }
        this.tenancyUpdated(tenancy)
        this.objectsByCategory[category].forEach((element: ObjectobjectObject) => {
          const newTenancy: ObjecttenancyTenancy = JSON.parse(JSON.stringify(tenancy))
          newTenancy.objectId = element.id
          newTenancy.ownerId = element.ownerId
          if (!this.newTenancies.find((x: ObjecttenancyTenancy) => x.objectId === element.id)) {
            this.newTenancies.push(newTenancy)
          } else {
            let list = JSON.parse(JSON.stringify(this.newTenancies))
            list = list.filter((x: ObjecttenancyTenancy) => x.objectId !== element.id)
            const createdTenancy = this.newTenancies.find((x: ObjecttenancyTenancy) => x.objectId === element.id)
            if (createdTenancy?.id) {
              newTenancy.id = createdTenancy.id
              newTenancy.numericId = createdTenancy.numericId
            }
            list.push(newTenancy)
            this.newTenancies = list
          }
        })
      } else {
        for (let category in this.objectsByCategory) {
          const tenancy = this.newTenancies.find((x: ObjecttenancyTenancy) => x.objectId === this.objectsByCategory[category][0].id)
          this.objectsByCategory[category].forEach((element: ObjectobjectObject) => {
            const newTenancy: ObjecttenancyTenancy = JSON.parse(JSON.stringify(tenancy))
            newTenancy.objectId = element.id
            newTenancy.ownerId = element.ownerId
            if (!this.newTenancies.find((x: ObjecttenancyTenancy) => x.objectId === element.id)) {
              this.newTenancies.push(newTenancy)
            } else {
              const index = this.newTenancies.findIndex((x: ObjecttenancyTenancy) => x.objectId === element.id)
              const createdTenancy = this.newTenancies[index]
              if (createdTenancy?.id) {
                newTenancy.id = createdTenancy.id
                newTenancy.numericId = createdTenancy.numericId
              }
              this.newTenancies[index] = newTenancy
            }
          })
        }
      }
    },
    isCurrentObjectsLoad(generation: number) {
      return generation === this.objectsLoadGeneration
    },
    dedupeObjectsById(objects: ObjectobjectObject[]): ObjectobjectObject[] {
      return [...new Set(objects.map((a: ObjectobjectObject) => a.id))].map((id: string) => {
        return objects.find((a: ObjectobjectObject) => a.id === id)
      })
    },
    async loadRequestObjectCache() {
      if (this.requestObjectId === '') {
        return
      }
      try {
        const rsp = await ObjectApi.listObjectsByIds([this.requestObjectId])
        this.listObjectsFromRequest = rsp.objects || []
      } catch (e) {
        console.log(e)
      }
    },
    async mergeRequestObjectIntoList(generation: number) {
      if (this.requestObjectId === '') {
        return
      }
      if (this.listObjects.some((o: ObjectobjectObject) => o.id === this.requestObjectId)) {
        return
      }
      if (this.listObjectsFromRequest.length === 0) {
        await this.loadRequestObjectCache()
      }
      if (!this.isCurrentObjectsLoad(generation)) {
        return
      }
      if (this.listObjectsFromRequest.length > 0) {
        this.listObjects = this.dedupeObjectsById(this.listObjects.concat(this.listObjectsFromRequest))
      }
    },
    reselectRequestObjectInTable(objIds: string[] = []) {
      if (this.requestObjectId === '' && objIds.length === 0) {
        return
      }
      this.$nextTick(() => {
        this.$refs['create-mass-rental-objects-table']?.selectObject(objIds)
      })
    },
    previousTenancyUpdated(tenancy: ObjecttenancyTenancy) {
      if (!tenancy?.id) {
        return
      }
      const idx = this.listTenancies.findIndex((t: ObjecttenancyTenancy) => t.id === tenancy.id)
      if (idx !== -1) {
        Vue.set(this.listTenancies, idx, tenancy)
      }
    },
    queuePreviousTenancyUpdate(tenancy: ObjecttenancyTenancy) {
      if (!tenancy?.id) {
        return
      }
      const idx = this.previousTenanciesToUpdate.findIndex((t: ObjecttenancyTenancy) => t.id === tenancy.id)
      if (idx !== -1) {
        Vue.set(this.previousTenanciesToUpdate, idx, tenancy)
      } else {
        this.previousTenanciesToUpdate.push(tenancy)
      }
    },
    unqueuePreviousTenancyUpdate(tenancyId: string) {
      const idx = this.previousTenanciesToUpdate.findIndex((t: ObjecttenancyTenancy) => t.id === tenancyId)
      if (idx !== -1) {
        this.previousTenanciesToUpdate.splice(idx, 1)
      }
    },
    getRequestObjectPreviousTenancy(): ObjecttenancyTenancy | null {
      if (this.source !== 'request' || this.requestObjectId === '') {
        return null
      }
      return this.getLastTenancies(this.requestObjectId).find((t: ObjecttenancyTenancy) => t && t.vacancy === false) || null
    },
    parseMassRentalDate(date: string | TypeDate): Date | null {
      const input = typeof date === 'string' ? date : this.$options.filters.objectToDateInput(date)
      const parsed = new Date(input)
      return isNaN(parsed.getTime()) ? null : parsed
    },
    isDesiredStartBeforePreviousTenancyStart(desiredStart: string | TypeDate): boolean {
      const previousTenancy = this.getRequestObjectPreviousTenancy()
      if (!previousTenancy?.startingAt) {
        return false
      }
      const desiredStartAt = this.parseMassRentalDate(desiredStart)
      const previousStartAt = this.parseMassRentalDate(previousTenancy.startingAt)
      if (!desiredStartAt || !previousStartAt) {
        return false
      }
      return desiredStartAt.getTime() < previousStartAt.getTime()
    },
    validateDesiredStartAgainstPreviousTenancyStart(): boolean {
      if (!this.isDesiredStartBeforePreviousTenancyStart(this.initialDesiredStartOfContract)) {
        return true
      }
      Vue.toasted.show(this.$t('message.massRental.contractStartBeforePreviousTenancyStart'), { type: 'error' })
      return false
    },
    autoAdjustPreviousTenancyEndDate(objectId: string, startDate: TypeDate) {
      if (this.source !== 'request' || this.requestObjectId === '' || objectId !== this.requestObjectId) {
        return
      }
      const previousTenancy = this.getLastTenancies(objectId).find((t: ObjecttenancyTenancy) => t && t.vacancy === false)
      if (!previousTenancy?.startingAt) {
        return
      }
      const newStartAt = this.parseMassRentalDate(startDate)
      const previousStartAt = this.parseMassRentalDate(previousTenancy.startingAt)
      if (!newStartAt || !previousStartAt) {
        return
      }
      if (newStartAt.getTime() <= previousStartAt.getTime()) {
        return
      }
      const previousEndAt = previousTenancy.endingAt ? this.parseMassRentalDate(previousTenancy.endingAt) : null
      const overlaps = previousEndAt ? previousEndAt.getTime() >= newStartAt.getTime() : false
      if (!overlaps) {
        this.unqueuePreviousTenancyUpdate(previousTenancy.id)
        return
      }
      const adjustedEndMs = newStartAt.getTime() - 86400000
      if (adjustedEndMs < previousStartAt.getTime()) {
        this.unqueuePreviousTenancyUpdate(previousTenancy.id)
        return
      }
      const adjusted = this.cloneTenancy(previousTenancy)
      adjusted.liabilityEndsAt = adjusted.endingAt || null
      adjusted.endingAt = this.toObject(adjustedEndMs)
      const tenancyIdx = this.listTenancies.findIndex((t: ObjecttenancyTenancy) => t.id === adjusted.id)
      if (tenancyIdx !== -1) {
        Vue.set(this.listTenancies, tenancyIdx, adjusted)
      }
      syncSubTenantEndingDates(adjusted)
      this.queuePreviousTenancyUpdate(adjusted)
    },
    resetObjectsLoadDerivedState() {
      if (this.tenancyIds !== '') {
        const splitIds = this.tenancyIds.split(',')
        this.listTenancies = this.listTenancies.filter((t: ObjecttenancyTenancy) => splitIds.includes(t.id))
      } else {
        this.listTenancies = []
      }
      this.listContacts = []
      this.initialTenancies = []
      this.newTenancies = []
      this.previousTenanciesToUpdate = []
    },
    onDesiredStartDateChanged(startDate: string) {
      this.initialDesiredStartOfContract = startDate
      this.pendingSelectedObjects = []
      this.objectsByCategory = null
      this.resetTenancyEditorsMountState()
      const debounced = this.debouncedLoadObjectsFromDesiredDate as ((d: string) => void) | undefined
      if (debounced) {
        debounced(startDate)
      } else {
        this.loadObjects(startDate).catch((e) => console.log(e))
      }
    },
    async loadObjects(startDate: string) {
      const generation = ++this.objectsLoadGeneration
      this.initialDesiredStartOfContract = startDate
      this.objectsLoading = true
      this.enrichmentLoading = false
      try {
        if (startDate !== '') {
          this.resetObjectsLoadDerivedState()
          this.listObjects = []
          const splitIds = this.buildingIds.split(',')
          const date = new Date(startDate)
          const dateObject = {
            year: date.getFullYear(),
            month: date.getMonth() + 1,
            day: date.getDate()
          }
          const response = await ObjectApi.listFreeByDate(splitIds, dateObject)
          if (!this.isCurrentObjectsLoad(generation)) {
            return
          }
          const objects = this.listObjectsFromTenancies.concat(this.listObjectsFromRequest).concat(response.objects)
          this.listObjects = this.dedupeObjectsById(objects)
          await this.mergeRequestObjectIntoList(generation)
          if (!this.isCurrentObjectsLoad(generation)) {
            return
          }
          const objectIds = this.listObjects.map((o: ObjectobjectObject) => o.id)

          // Progressive paint: show object rows as soon as free-list is ready
          if (this.requestObjectId !== '') {
            this.reselectRequestObjectInTable()
          }
          this.objectsLoading = false
          await this.$nextTick()
          if (!this.isCurrentObjectsLoad(generation)) {
            return
          }

          // Tenancies / owner enrich rows and drafts without blocking the table paint
          this.enrichmentLoading = true
          try {
            await Promise.all([
              objectIds.length > 0
                ? this.loadTenantContacts(objectIds, dateObject, generation)
                : Promise.resolve().then(() => {
                  if (this.requestObjectId !== '') {
                    this.reselectRequestObjectInTable()
                  }
                }),
              this.loadOwnerAccount(generation)
            ])
            if (!this.isCurrentObjectsLoad(generation)) {
              return
            }
            if (this.requestObjectId !== '') {
              this.reselectRequestObjectInTable()
            }
            this.reapplySelectedObjectsFromTable()
          } finally {
            if (this.isCurrentObjectsLoad(generation)) {
              this.enrichmentLoading = false
            }
          }
        } else {
          if (!this.isCurrentObjectsLoad(generation)) {
            return
          }
          this.listObjects = []
          this.listTenancies = []
          this.initialTenancies = []
          this.newTenancies = []
          this.listContacts = []
          this.owner = null
          this.pendingSelectedObjects = []
          this.objectsByCategory = null
          this.resetTenancyEditorsMountState()
          this.tenantContacts = []
          this.billingContact = null
          this.reapplySelectedObjectsFromTable()
        }
      } finally {
        if (this.isCurrentObjectsLoad(generation)) {
          this.objectsLoading = false
          this.enrichmentLoading = false
        }
      }
    },
    async loadBuildings() {
      try {
        if (this.buildingIds === '') {
          return
        }
        const response = await ObjectApi.getBuildingsListByIds(this.buildingIds.split(','))
        this.listBuildings = response.buildings
      } catch (e) {
        console.log(e)
      }
    },
    async loadProperties() {
      try {
        if (this.buildingIds === '') {
          return
        }
        const response = await ObjectApi.getPropertiesByBuildingIds(this.buildingIds.split(','))
        this.listProperties = response.properties
      } catch (e) {
        console.log(e)
      }
    },
    async loadOwnerAccount(generation?: number) {
      if (this.listObjects.length > 0) {
        const owner = await UserApi.getAccount(this.listObjects[0].ownerId)
        if (generation !== undefined && !this.isCurrentObjectsLoad(generation)) {
          return
        }
        this.owner = owner.account
      }
    },
    async loadTenantContacts(objectIds: string[], startDate: TypeDate, generation?: number) {
      let tenantContactIds = []
      try {
        if (objectIds.length === 0) {
          this.reselectRequestObjectInTable()
          return
        }
        const tenancyResponse = await ObjectApi.getTenancies(objectIds)
        if (generation !== undefined && !this.isCurrentObjectsLoad(generation)) {
          return
        }

        const list: ObjecttenancyTenancy[] = this.listTenancies.concat(tenancyResponse.tenancies)

        this.listTenancies = [...new Set(list.map((a) => a.id))].map(id => {
          return list.find((a) => a.id === id)
        })
        tenantContactIds = this.listTenancies.map((t: ObjecttenancyTenancy) => t?.tenant?.contactIds || []).flat()
        this.initialTenancies = []
        const today = new Date()
        const objDate = {
          year: today.getFullYear(),
          month: today.getMonth() + 1,
          day: today.getDate()
        }
        const tenanciesByObjectId: Record<string, ObjecttenancyTenancy[]> = {}
        for (const tenancy of this.listTenancies) {
          if (!tenancy?.objectId) {
            continue
          }
          if (!tenanciesByObjectId[tenancy.objectId]) {
            tenanciesByObjectId[tenancy.objectId] = []
          }
          tenanciesByObjectId[tenancy.objectId].push(tenancy)
        }
        const t = this
        const lastTenanciesForObject = (objectId: string) => {
          const objectTenancies = tenanciesByObjectId[objectId] || []
          return [...objectTenancies].sort(function (a: ObjecttenancyTenancy, b: ObjecttenancyTenancy) {
            return new Date(t.$options.filters.objectToDateInput(b.startingAt)).getTime() - new Date(t.$options.filters.objectToDateInput(a.startingAt)).getTime()
          }).slice(0, 2)
        }
        const objectsById: Record<string, ObjectobjectObject> = {}
        for (const obj of this.listObjects) {
          if (obj?.id) {
            objectsById[obj.id] = obj
          }
        }
        objectIds.forEach((element: string) => {
          this.autoAdjustPreviousTenancyEndDate(element, startDate)
          if (this.tenancyIds !== '') {
            const splitIds = this.tenancyIds.split(',')
            const tenancy = (tenanciesByObjectId[element] || []).find((x: ObjecttenancyTenancy) => splitIds.includes(x.id))
            if (tenancy) {
              this.initialTenancies.push(this.cloneTenancy(tenancy))
              return
            }
          }
          const lastTenancies = lastTenanciesForObject(element)
          if (!this.initialTenancies.find((x: ObjecttenancyTenancy) => x.objectId === element) && lastTenancies.length > 0) {
            const lastTenancy: ObjecttenancyTenancy = lastTenancies[0]
            const createdTenancy = this.setDefaultValuesFromPreviousTenancy(element, lastTenancy)
            createdTenancy.internalId = Math.random().toString(36).substring(2, 9)
            createdTenancy.startingAt = startDate
            createdTenancy.objectId = element
            createdTenancy.rent.adjustments[0].byDate = startDate
            createdTenancy.rent.adjustments[0].newItem = true
            createdTenancy.ownerId = lastTenancy.ownerId
            this.initialTenancies.push(createdTenancy)
          } else {
            const createdTenancy = JSON.parse(JSON.stringify(this.emptyTenancy))
            createdTenancy.internalId = Math.random().toString(36).substring(2, 9)
            createdTenancy.startingAt = startDate
            createdTenancy.objectId = element
            createdTenancy.rent.adjustments[0].byDate = startDate
            createdTenancy.rent.adjustments[0].newItem = true
            createdTenancy.contract.creationDate = objDate
            createdTenancy.ownerId = objectsById[element]?.ownerId
            this.initialTenancies.push(createdTenancy)
          }
        })
        if (this.newTenancies.length > 0) {
          this.newTenancies.forEach((t: ObjecttenancyTenancy, idx: number) => {
            const fresh = this.initialTenancies.find((i: ObjecttenancyTenancy) => i.objectId === t.objectId)
            if (fresh?.rent?.adjustments?.[0]) {
              const merged = JSON.parse(JSON.stringify(t)) as ObjecttenancyTenancy
              merged.startingAt = fresh.startingAt
              merged.rent.adjustments[0].byDate = fresh.rent.adjustments[0].byDate
              this.copyDraftTenantFields(fresh, merged)
              Vue.set(this.newTenancies, idx, merged)
            }
          })
        }
      } catch (e) {
        console.log(e)
        Vue.toasted.show(this.$t('message.loadingErrors.tenancy'), { type: 'error' })
      }

      if (this.tenantIds !== '') {
        const splitIds = this.tenantIds.split(',')
        this.forEachEditableDraftTenancy((t: ObjecttenancyTenancy) => {
          t.tenant.contactIds = splitIds
          t.tenant.billingContactId = splitIds[0]
        })
      }

      if (this.tenantIds !== '') {
        await this.loadRequestTenantContacts(generation)
      } else if (tenantContactIds && tenantContactIds.length > 0) {
        this.loadContacts(tenantContactIds, generation)
      }
      if (generation !== undefined && !this.isCurrentObjectsLoad(generation)) {
        return
      }
      let objIds = [] as string[]
      if (this.tenancyIds !== '') {
        const splitIds = this.tenancyIds.split(',')
        objIds = this.listTenancies.filter((t: ObjecttenancyTenancy) => splitIds.includes(t.id)).map((x: ObjecttenancyTenancy) => x.objectId)
      }
      this.reselectRequestObjectInTable(objIds)
    },
    async loadRequestTenantContacts(generation?: number) {
      if (this.tenantIds === '') {
        return
      }
      const splitIds = this.tenantIds.split(',').filter((id: string) => id)
      if (splitIds.length === 0) {
        return
      }
      try {
        const response = await ContactApi.contactResolve(splitIds)
        if (generation !== undefined && !this.isCurrentObjectsLoad(generation)) {
          return
        }
        const list: Contact[] = this.listContacts.concat(response.persons).concat(response.companies)
        this.listContacts = [...new Set(list.map((a) => a.id))].map(id => {
          return list.find((a) => a.id === id)
        })
        this.tenantContacts = splitIds
          .map((id: string) => this.listContacts.find((c: Contact) => c.id === id))
          .filter((c: Contact | undefined): c is Contact => !!c)
        if (this.tenantContacts.length > 0) {
          this.billingContact = this.tenantContacts[0]
        }
      } catch (e) {
        console.log(e)
        Vue.toasted.show(this.$t('message.loadingErrors.contacts'), { type: 'error' })
      }
    },
    loadContacts(ids: string[], generation?: number) {
      if (ids.length > 0) {
        ContactApi.contactResolve(ids).then((response: V2ResolveResponse) => {
          if (generation !== undefined && !this.isCurrentObjectsLoad(generation)) {
            return
          }
          const list: Contact[] = this.listContacts.concat(response.persons).concat(response.companies)
          this.listContacts = [...new Set(list.map((a) => a.id))].map(id => {
            return list.find((a) => a.id === id)
          })
        }).catch((e: GooglerpcStatus) => {
          console.log(e)
          Vue.toasted.show(this.$t('message.loadingErrors.contacts'), { type: 'error' })
        })
      }
    },
    customSort(item: string) {
      if (item.includes('COMMERCIAL')) {
        return 1
      } else if (item.includes('APARTMENT')) {
        return 2
      } else if (item.includes('SECONDARY_ROOM')) {
        return 3
      } else if (item.includes('PARKING_SPACE')) {
        return 4
      } else {
        return [5, item] // Alphabetical sorting for other types
      }
    },
    getLastTenancies(objectId: string) {
      const t = this
      return [...this.listTenancies.filter((x: ObjecttenancyTenancy) => x.objectId === objectId).sort(function (a: ObjecttenancyTenancy, b: ObjecttenancyTenancy) {
        return new Date(t.$options.filters.objectToDateInput(b.startingAt)).getTime() - new Date(t.$options.filters.objectToDateInput(a.startingAt)).getTime()
      }).slice(0, 2)]
    },
    tenantChanged(contact: Contact) {
      if (!this.objectOwnerContacts.find(x => x.id === contact.id || x.objectID === contact.id)) {
        this.setObjectOwnerContacts(this.objectOwnerContacts.concat(contact))
      }
      if (!this.listContacts.find(x => x.id === contact.id)) {
        this.listContacts.push(contact)
      }
      let updatedContact = false
      this.tenantContacts.forEach((value: Contact, index: number) => {
        if (value.id === contact.id) {
          updatedContact = true
          Vue.set(this.tenantContacts, index, contact)
        }
      })

      if (!updatedContact) {
        this.tenantContacts.push(contact)
      }
      if (this.initialTenancies.length > 0) {
        this.forEachEditableDraftTenancy((t: ObjecttenancyTenancy) => {
          const ids: string[] = this.tenantContacts.map((value: Contact) => value.id)
          t.tenant.contactIds = [...new Set(ids)]
        })
        this.syncNewTenanciesFromDrafts()
      }
    },
    tenantRemoved(id: string) {
      this.tenantContacts = this.tenantContacts.filter((c: Contact) => c.id !== id)
      if (this.initialTenancies.length > 0) {
        this.forEachEditableDraftTenancy((t: ObjecttenancyTenancy) => {
          const ids: string[] = this.tenantContacts.map((value: Contact) => value.id)
          t.tenant.contactIds = [...new Set(ids)]
        })
        this.syncNewTenanciesFromDrafts()
      }
    },
    additionalContactChanged(data: { type: string, contact: Contact }) {
      const type = data.type
      if (!this.objectOwnerContacts.find(x => x.id === data.contact.id || x.objectID === data.contact.id)) {
        this.setObjectOwnerContacts(this.objectOwnerContacts.concat(data.contact))
      }
      if (!this.listContacts.find(x => x.id === data.contact.id)) {
        this.listContacts.push(data.contact)
      }
      if (type === 'BILLING_CONTACT') {
        Vue.set(this, 'billingContact', data.contact)
        // Add tenant (if not existing) the same as billing contact
        let exist = false
        this.tenantContacts.forEach((value: Contact) => {
          if (value.id === data.contact.id) {
            exist = true
            this.billingContact = data.contact
          }
        })
        if (!exist) {
          this.tenantContacts.push(data.contact)
        }
        if (this.initialTenancies.length > 0) {
          this.forEachEditableDraftTenancy((t: ObjecttenancyTenancy) => {
            t.tenant.billingContactId = data.contact.id
          })
        }
      }

      if (type === 'CORRESPONDENCE_CONTACT') {
        Vue.set(this, 'correspondenceContact', data.contact)
        this.correspondenceContact = data.contact
        if (this.initialTenancies.length > 0) {
          this.forEachEditableDraftTenancy((t: ObjecttenancyTenancy) => {
            t.tenant.correspondenceContactId = data.contact.id
          })
        }
      }

      if (type === 'GUARANTOR_CONTACT') {
        let updatedContact = false
        this.guarantorContacts.forEach((value: Contact, index: number) => {
          if (value.id === data.contact.id) {
            updatedContact = true
            Vue.set(this.guarantorContacts, index, data.contact)
          }
        })

        if (!updatedContact) {
          this.guarantorContacts.push(data.contact)
        }

        if (this.initialTenancies.length > 0) {
          this.forEachEditableDraftTenancy((t: ObjecttenancyTenancy) => {
            const ids: string[] = this.guarantorContacts.map((value: Contact) => value.id)
            t.tenant.guarantorContactIds = [...new Set(ids)]
          })
        }
      }
      if (this.initialTenancies.length > 0) {
        this.syncNewTenanciesFromDrafts()
      }
    },
    removeAdditionalContact(data: { type: string, contact: Contact }) {
      const id = data.contact.id
      const type = data.type

      if (type === 'BILLING_CONTACT') {
        if (this.billingContact.id === id) {
          Vue.set(this, 'billingContact', null)
        }
      }

      if (type === 'CORRESPONDENCE_CONTACT') {
        if (this.correspondenceContact.id === id) {
          Vue.set(this, 'correspondenceContact', null)
        }
      }

      if (type === 'GUARANTOR_CONTACT') {
        const index = this.guarantorContacts.findIndex((c: Contact) => c.id === id)
        Vue.delete(this.guarantorContacts, index)
      }
    },
    shouldReloadObjectsForStep1(): boolean {
      if (!this.initialDesiredStartOfContract) {
        return true
      }
      return this.listObjects.length === 0
    },
    async nextStep(props: WizardParams) {
      if (props.activeTabIndex === 0) {
        const debounced = this.debouncedLoadObjectsFromDesiredDate as { clear: () => void } | undefined
        debounced?.clear()
        if (this.shouldReloadObjectsForStep1()) {
          await this.loadObjects(this.initialDesiredStartOfContract)
        }
        if (this.$refs['create-mass-rental-objects-table']) {
          if (this.$refs['create-mass-rental-objects-table'].isInvalid()) {
            return
          }
        }
        if (!this.hasPendingSelectedObjects) {
          return
        }
        if (!this.validateDesiredStartAgainstPreviousTenancyStart()) {
          return
        }
        this.commitPendingSelectionToCategories()
        props.nextTab()
        this.scrollToTop()
        this.countTabs = this.$refs.wizard?.tabs?.length || 0
        this.$nextTick(() => {
          if (this.tenantContactsMissingAddress && this.tenantContacts.length > 0) {
            Vue.toasted.show(this.$t('message.savingErrors.tenantHaveAddress'), { type: 'error' })
          }
        })
        return
      }
      if (props.activeTabIndex === 1) {
        const hasNoAddress = this.tenantContacts.filter((x: Contact) => !(x.address?.zip && x.address?.city && x.address?.country))
        if (hasNoAddress.length > 0) {
          Vue.toasted.show(this.$t('message.savingErrors.tenantHaveAddress'), { type: 'error' })
          return
        }
        if (!this.billingContact) {
          Vue.toasted.show(this.$t('message.savingErrors.hasNoDebtor'), { type: 'error' })
          return
        }
        if (!(this.billingContact.address?.zip && this.billingContact.address?.city && this.billingContact.address?.country)) {
          Vue.toasted.show(this.$t('message.savingErrors.debtorHaveAddress'), { type: 'error' })
          return
        }
        if (this.correspondenceContact && !(this.correspondenceContact.address?.zip && this.correspondenceContact.address?.city && this.correspondenceContact.address?.country)) {
          Vue.toasted.show(this.$t('message.savingErrors.correspondenceHaveAddress'), { type: 'error' })
          return
        }
        if (this.guarantorContacts.some((x: Contact) => !(x.address?.zip && x.address?.city && x.address?.country))) {
          Vue.toasted.show(this.$t('message.savingErrors.guarantorHaveAddress'), { type: 'error' })
          return
        }
        this.syncNewTenanciesFromDrafts()
        // Mount all tenancy editors when entering category tabs (not on step 1→2)
        this.tenancyEditorsLoading = true
        await this.yieldForPaint()
        this.tenancyEditorsMounted = true
        await this.$nextTick()
        props.nextTab()
        this.scrollToTop()
        this.countTabs = this.$refs.wizard?.tabs?.length || 0
        this.tenancyEditorsLoading = false
        return
      }
      props.nextTab()
      this.scrollToTop()
      this.countTabs = this.$refs.wizard?.tabs?.length || 0
    },
    nextStepWithValidation(props: WizardParams) {
      if (props.activeTabIndex > 1) {
        const index = props.activeTabIndex - 2
        const tabs = this.$refs['tenancy-tab-' + index]
        if (tabs && tabs.length > 0) {
          for (let i = 0; i < tabs.length; i++) {
            if (tabs[i].isInvalid()) {
              return
            }
          }
          this.$nextTick(() => {
            props.nextTab()
            this.scrollToTop()
          })
        }
      }
      this.countTabs = this.$refs.wizard?.tabs?.length || 0
    },
    backClicked(props: WizardParams) {
      props.prevTab()
      this.scrollToTop()
    },
    scrollToTop() {
      this.$nextTick(() => {
        const domRect = this.$el.querySelector('#wizard')
        domRect.scrollIntoView(true)
      })
    },
    setDefaultValuesFromPreviousTenancy(id: string, lastTenancy: ObjecttenancyTenancy) {
      const createdTenancy = JSON.parse(JSON.stringify(this.emptyTenancy))
      if (lastTenancy) {
        const current = this
        createdTenancy.contract.creationDate = this.toObject(new Date())
        if (lastTenancy.rent.adjustments.length > 0) {
          const previousAdjustment = lastTenancy.rent.adjustments.length > 1
            ? lastTenancy.rent.adjustments.sort(function (a, b) {
              return (b.byDate ? new Date(current.$options.filters.objectToDateInput(b.byDate)).getTime() : 0) - (a.byDate ? new Date(current.$options.filters.objectToDateInput(a.byDate)).getTime() : 0)
            })[0]
            : lastTenancy.rent.adjustments[0]
          if (previousAdjustment) {
            for (let i = 0; i < previousAdjustment.advancePaymentValues.length; i++) {
              createdTenancy.rent.adjustments[0].advancePaymentValues.push(previousAdjustment.advancePaymentValues[i])
            }
            for (let i = 0; i < previousAdjustment.flatRateValues.length; i++) {
              createdTenancy.rent.adjustments[0].flatRateValues.push(previousAdjustment.flatRateValues[i])
            }
            for (let i = 0; i < previousAdjustment.additionalValues.length; i++) {
              createdTenancy.rent.adjustments[0].additionalValues.push(previousAdjustment.additionalValues[i])
            }

            createdTenancy.rent.adjustments[0].coldRentPerMonth = previousAdjustment.coldRentPerMonth
            createdTenancy.rent.adjustments[0].coldRent = previousAdjustment.coldRent
            createdTenancy.rent.adjustments[0].coldRentM2PerYear = previousAdjustment.coldRentM2PerYear
            createdTenancy.rent.adjustments[0].calculationBasis = previousAdjustment.calculationBasis
            createdTenancy.rent.adjustments[0].additionalCostsBillingType = previousAdjustment.additionalCostsBillingType
            const lastestTenant = this.listTenancies.filter((x) => x.objectId === id && !x.internalId).sort(function (a, b) {
              return b.endingAt - a.endingAt
            })
            if (lastTenancy.vacancy === true && lastestTenant.length > 1 && lastestTenant[1].terminationType === 'TERMINATION_TYPE_OFF_TERM') {
              const lastAdjustement = lastestTenant[1].rent.adjustments.sort(function (a, b) {
                return b.byDate - a.byDate
              })[0]
              const lastTenant = lastestTenant[1]
              createdTenancy.rent.adjustments[0].coldRentPerMonth = lastAdjustement.coldRentPerMonth
              createdTenancy.rent.adjustments[0].coldRentM2PerYear = lastAdjustement.coldRentM2PerYear
              createdTenancy.rent.adjustments[0].coldRent = lastAdjustement.coldRent
              createdTenancy.rent.adjustments[0].calculationBasis = lastAdjustement.calculationBasis
              createdTenancy.rent.adjustments[0].additionalCostsBillingType = lastAdjustement.additionalCostsBillingType

              createdTenancy.rent.adjustments[0].retentionOfRents = lastAdjustement.retentionOfRents

              createdTenancy.rent.adjustments[0].referenceInterestRate.rate = lastAdjustement.referenceInterestRate.rate
              createdTenancy.rent.adjustments[0].referenceInterestRate.date = lastAdjustement.referenceInterestRate.date
              createdTenancy.rent.adjustments[0].referenceInterestRate.ratePossible = lastAdjustement.referenceInterestRate.ratePossible
              createdTenancy.rent.adjustments[0].countryIndex.date = lastAdjustement.countryIndex.date
              createdTenancy.rent.adjustments[0].countryIndex.baseDate = lastAdjustement.countryIndex.baseDate
              createdTenancy.rent.adjustments[0].costIncreaseType = lastAdjustement.costIncreaseType
              createdTenancy.rent.adjustments[0].costIncreaseDate = lastAdjustement.costIncreaseDate
              createdTenancy.rent.adjustments[0].costIncreaseValue = lastAdjustement.costIncreaseValue
              createdTenancy.rent.adjustments[0].dueDates = lastAdjustement.dueDates
              createdTenancy.rent.adjustments[0].countryIndex.inflation = lastAdjustement.countryIndex.inflation
              createdTenancy.terminationLandlord = lastTenant?.terminationLandlord
              createdTenancy.terminationTenant = lastTenant?.terminationTenant
            }
            if (lastTenancy.terminationType === 'TERMINATION_TYPE_OFF_TERM') {
              createdTenancy.rent.adjustments[0].coldRentPerMonth = previousAdjustment.coldRentPerMonth
              createdTenancy.rent.adjustments[0].coldRent = previousAdjustment.coldRent
              createdTenancy.rent.adjustments[0].coldRentM2PerYear = previousAdjustment.coldRentM2PerYear
              createdTenancy.rent.adjustments[0].calculationBasis = previousAdjustment.calculationBasis
              createdTenancy.rent.adjustments[0].additionalCostsBillingType = previousAdjustment.additionalCostsBillingType

              createdTenancy.rent.adjustments[0].retentionOfRents = previousAdjustment.retentionOfRents

              createdTenancy.rent.adjustments[0].referenceInterestRate.rate = previousAdjustment.referenceInterestRate.rate
              createdTenancy.rent.adjustments[0].referenceInterestRate.date = previousAdjustment.referenceInterestRate.date
              createdTenancy.rent.adjustments[0].referenceInterestRate.ratePossible = previousAdjustment.referenceInterestRate.ratePossible
              createdTenancy.rent.adjustments[0].countryIndex.date = previousAdjustment.countryIndex.date
              createdTenancy.rent.adjustments[0].countryIndex.baseDate = previousAdjustment.countryIndex.baseDate
              createdTenancy.rent.adjustments[0].costIncreaseType = previousAdjustment.costIncreaseType
              createdTenancy.rent.adjustments[0].costIncreaseDate = previousAdjustment.costIncreaseDate
              createdTenancy.rent.adjustments[0].costIncreaseValue = previousAdjustment.costIncreaseValue
              createdTenancy.rent.adjustments[0].dueDates = previousAdjustment.dueDates
              createdTenancy.rent.adjustments[0].countryIndex.inflation = previousAdjustment.countryIndex.inflation
              createdTenancy.terminationLandlord = lastTenancy?.terminationLandlord
              createdTenancy.terminationTenant = lastTenancy?.terminationTenant
            }
            if (lastTenancy.vacancy === true && lastestTenant.length > 1) {
              createdTenancy.terminationLandlord = lastestTenant[1]?.terminationLandlord
              createdTenancy.terminationLandlord.earliestDate = null
              createdTenancy.terminationTenant = lastestTenant[1]?.terminationTenant
              createdTenancy.terminationTenant.earliestDate = null
            } else {
              createdTenancy.terminationLandlord = lastTenancy?.terminationLandlord
              createdTenancy.terminationLandlord.earliestDate = null
              createdTenancy.terminationTenant = lastTenancy?.terminationTenant
              createdTenancy.terminationTenant.earliestDate = null
            }
          }
        }

        createdTenancy.rent.additionalCostsBillingType = lastTenancy.rent.additionalCostsBillingType
        if (lastTenancy.contract.rentType) {
          createdTenancy.contract.rentType = lastTenancy.contract.rentType
        }
        this.$nextTick(() => {
          if (lastTenancy.contract.typeOfUse) {
            createdTenancy.contract.typeOfUse = lastTenancy.contract.typeOfUse
            if (lastTenancy.contract.typeOfUse === 'TYPE_OF_USE_PARKING_EXTERNAL') {
              const property = this.getPropertyForObject(lastTenancy.objectId)
              if (property) {
                createdTenancy.taxable = !property.opted
              }
            }
          }
        })
        return createdTenancy
      }
    },
    saveTenancies(props: WizardParams) {
      const index = props.activeTabIndex - 2
      const tabs = this.$refs['tenancy-tab-' + index]
      if (tabs && tabs.length > 0) {
        for (let i = 0; i < tabs.length; i++) {
          if (tabs[i].isInvalid()) {
            return
          }
        }
        this.$nextTick(() => {
          this.disableButtons = true
          const promises: any = []
          const tenancyByObjectId: Map<string, ObjecttenancyTenancy> = new Map(
            this.newTenancies.map((t: ObjecttenancyTenancy) => [t.objectId, t] as [string, ObjecttenancyTenancy])
          )
          const draftObjectIds = new Set(this.newTenancies.map((t: ObjecttenancyTenancy) => t.objectId))
          const previousTenancyUpdateCount = this.previousTenanciesToUpdate.length
          this.previousTenanciesToUpdate.forEach((tenancy: ObjecttenancyTenancy) => {
            promises.push(ObjectApi.updateTenancy(tenancy))
          })
          let newTenanciesCreated = false
          this.newTenancies.forEach((tenancy: ObjecttenancyTenancy) => {
            if ((this.tenancyIds !== '' && tenancy.id !== '' && this.tenancyIds.includes(tenancy.id)) || (tenancy.id !== '' && this.createdTenancies.length > 0)) {
              promises.push(ObjectApi.updateTenancy(tenancy))
            } else {
              promises.push(ObjectApi.createTenancy(tenancy))
              newTenanciesCreated = true
            }
          })
          Promise.all(promises).then((response) => {
            const tenancyIds: string[] = []
            const nextNewTenancies: ObjecttenancyTenancy[] = []
            const nextCreatedTenancies: ObjecttenancyTenancy[] = []
            response.slice(0, previousTenancyUpdateCount).forEach((element: ObjecttenancyCreateResponse) => {
              if (element?.tenancy) {
                this.previousTenancyUpdated(element.tenancy)
              }
            })
            this.previousTenanciesToUpdate = []
            response.slice(previousTenancyUpdateCount).forEach((element: ObjecttenancyCreateResponse) => {
              if (!element?.tenancy) {
                return
              }
              if (!draftObjectIds.has(element.tenancy.objectId)) {
                return
              }
              const draft = tenancyByObjectId.get(element.tenancy.objectId)
              const isCreated = !draft?.id
              nextNewTenancies.push(element.tenancy)
              if (isCreated) {
                nextCreatedTenancies.push(element.tenancy)
                tenancyIds.push(element.tenancy.id)
              }
            })
            this.newTenancies = nextNewTenancies
            this.createdTenancies = this.createdTenancies.concat(nextCreatedTenancies)
            if (this.source === 'request' && this.requestId !== '' && newTenanciesCreated) {
              RequestApi.requestReserve(this.requestId, tenancyIds)
            }
            this.disableButtons = false
            props.nextTab()
            this.scrollToTop()
          }).catch((error) => {
            this.disableButtons = false
            console.log(error)
          })
        })
      }
    },
    addDocumentToTenancy(value: boolean) {
      this.addDocumentToContact = value
    },
    showInPortalMethod(value: boolean) {
      this.showInPortal = value
    },
    contractSelectedAction(ids: string[], contracts: Contractv2ContractTemplate[]) {
      this.contractSelected = ids
      this.contracts = contracts
    },
    selectedContractPerObject(value: string[], contracts: Contractv2ContractTemplate[], objectId: string) {
      this.$set(this.selectedContractsByObject, objectId, { ids: value, contracts: contracts })
    },
    templateOwnersIdsForObject(object: ObjectobjectObject) {
      return [].concat(parseInt(object.ownerId))
    },
    renderContract() {
      this.disableButtons = true
      const array = []
      const promises = []
      const listTemplatePdf = []
      if (this.showSeparateDocumentsOption && this.generateSeparateDocumentsPerObject) {
        this.createDocumentObjects.forEach((object: ObjectobjectObject) => {
          const selection = this.selectedContractsByObject[object.id]
          const selectedIds = selection?.ids || []
          const selectedContracts = selection?.contracts || []
          const tenancy = this.createdTenancies.find((item: ObjecttenancyTenancy) => item.objectId === object.id)
          if (!tenancy) {
            return
          }
          selectedIds.forEach((selectedId: string) => {
            const signatories: string[] = []
            const contract = selectedContracts.find(x => x.id === selectedId)
            if (contract && !contract.mediaId.includes('.pdf')) {
              if (contract.firstSignature !== '') {
                signatories.push(contract.firstSignature)
              }
              if (contract.secondSignature) {
                if (contract.firstSignature === '') {
                  signatories.push('')
                }
                signatories.push(contract.secondSignature)
              }
              array.push({
                contract: selectedId,
                objectId: object.id,
                template: contract
              })
              promises.push(ObjectApi.RenderTenancyTemplate(
                selectedId,
                tenancy.id || tenancy.objectID,
                this.addDocumentToContact,
                this.showInPortal,
                this.title,
                this.description,
                '',
                null,
                signatories,
                false,
                contract.enforcePdf
              ))
            } else if (contract) {
              listTemplatePdf.push(contract.mediaId)
            }
          })
        })
      } else {
        for (let index = 0; index < this.contractSelected.length; index++) {
            const signatories = []
            const contract = this.contracts.find(x => x.id === this.contractSelected[index])
            if (contract && !contract.mediaId.includes('.pdf')) {
              if (contract && contract.firstSignature !== '') {
                signatories.push(contract.firstSignature)
              }
              if (contract && contract.secondSignature) {
                if (contract && contract.firstSignature === '') {
                  signatories.push('')
                }
                signatories.push(contract.secondSignature)
              }
              array.push({
                contract: this.contractSelected[index],
                template: contract
              })
              promises.push(ObjectApi.renderTenanciesTemplate(this.contractSelected[index], this.createdTenancies.map(tenancy => tenancy.id || tenancy.objectID), this.title, this.description, '', signatories, '', this.addDocumentToContact, this.showInPortal, 'RENDER_ORDER_DEFAULT'))
            } else if (contract) {
              listTemplatePdf.push(contract.mediaId)
            }
          }
        }
      if (listTemplatePdf.length > 0) {
        MediaApi.getListMedia(listTemplatePdf).then(response => {
          for (const media of response.media) {
            saveAs(media.url, media.filename)
          }
        })
      }
      if (this.hasSelectedContracts && promises.length === 0) {
        this.disableButtons = false
        Vue.toasted.show(this.$t('message.savingErrors.general'), { type: 'error' })
        return
      }
      Promise.all(promises)
        .then(response => {
          const renderId = []
          let downloadName = ''
          response.forEach((element, index) => {
            const contract = array[index].template
            // const currentTenancy = this.createdTenancies.find(x => x.id === array[index].tenancyId || x.objectID === array[index].tenancyId)
            // const object = this.listObjects.find(x => x.id === currentTenancy.objectId)
            // if (currentTenancy.tenant.contactIds.length > 0 && this.listContacts.length > 0) {
            //   let nameOfTenant = ''
            //   currentTenancy.tenant.contactIds.forEach((element, index) => {
            //     if (index > 0) {
            //       nameOfTenant = nameOfTenant + '_'
            //     }
            //     nameOfTenant = nameOfTenant + this.listContacts.find(x => x.id === element).name
            //   })
            //   downloadName = contract.title + '_' + nameOfTenant + '_' + object.numericId
            // } else {
              downloadName = contract.title
              if (this.showSeparateDocumentsOption && this.generateSeparateDocumentsPerObject) {
                const selectedObject = this.listObjects.find(x => x.id === array[index].objectId)
                if (selectedObject && selectedObject.numericId) {
                  downloadName = `${downloadName}_${selectedObject.numericId}`
                }
              }
            // }
            const item = { renderId: element.renderId + '_' + downloadName + '-/-' + contract.enforcePdf, contract: contract }
            renderId.push(item)
          })
          const currentRenderIds = JSON.parse(localStorage.getItem('renderId') || '[]')
          const mergedRenderIds = Array.isArray(currentRenderIds) ? [...currentRenderIds, ...renderId] : renderId
          localStorage.setItem('renderId', JSON.stringify(mergedRenderIds))

          this.redirectOnFinish()
        })
        .catch((e) => {
          this.loading = false
          this.disableButtons = false
          console.log(e)
          Vue.toasted.show(this.$t('message.savingErrors.general'), { type: 'error' })
        })
    },
    redirectOnFinish() {
      setTimeout(() => {
        const debtorName: string = this.billingContact.name !== '' ? this.billingContact.name : this.billingContact.lastName + ' ' + this.billingContact.firstName
        if (this.isOwnerModule) {
          this.$router.push({
            name: 'OwnerTenanciesView',
            query: { query: debtorName }
          })
        } else if (this.isAssetModule) {
          this.$router.push({
            name: 'AssetTenanciesView',
            query: { query: debtorName }
          })
        } else if (this.isAdminModule) {
          this.$router.push({
            name: 'AdminTenanciesView',
            query: { query: debtorName }
          })
        } else if (this.isAccountingModule) {
          this.$router.push({
            name: 'AccountingTenanciesView',
            query: { query: debtorName }
          })
        } else if (this.isMarketingModule) {
          this.$router.push({
            name: 'MarketingObjectDetailsView',
            params: { id: this.requestObjectId },
            query: { view: '0', elementSelected: this.requestId }
          })
        }
      }, 2000)
    }
  }
})
</script>

<style lang="scss" scoped>
.mass-rental-loading-overlay {
  position: fixed;
  inset: 0;
  z-index: 1050;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.7);
}
</style>
